import { dateKeyFromString } from './reportDates';
import { isBrickFactoryUser } from './businessType';
import { hasTenantModule } from './tenantModules';
import { wakalaCashbookRows } from '../wakalaConstants';

export function monthKeysEndingToday(count) {
  const keys = [];
  const d = new Date();
  for (let i = 0; i < count; i += 1) {
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    keys.unshift(`${yy}-${mm}`);
    d.setMonth(d.getMonth() - 1);
  }
  return keys;
}

export function periodLabelFromMonths(monthKeys) {
  if (!monthKeys?.length) return '';
  const first = monthKeys[0];
  const last = monthKeys[monthKeys.length - 1];
  if (first === last) return first;
  return `${first} to ${last}`;
}

function saleProfit(s) {
  const p = parseFloat(s.profit);
  if (!Number.isNaN(p)) return p;
  const a = parseFloat(s.amount);
  const c = parseFloat(s.cost);
  if (!Number.isNaN(a) && !Number.isNaN(c)) return a - c;
  return 0;
}

function emptyMonthRow() {
  return {
    posRevenue: 0,
    posProfit: 0,
    posCount: 0,
    expenses: 0,
    expenseBoss: 0,
    expenseStaff: 0,
    expenseOffice: 0,
    printBilled: 0,
    printCollected: 0,
    otherServices: 0,
    wakalaIn: 0,
    wakalaOut: 0,
    bricksNet: 0,
    deliveryRevenue: 0,
    deliveryPieces: 0,
  };
}

export function buildLoanPackSnapshot({
  monthCount,
  salesHistory,
  expenses,
  products,
  printJobs,
  otherServiceSales,
  transactions,
  factoryByMonth,
  debtSummary,
  user,
}) {
  const monthKeys = monthKeysEndingToday(monthCount);
  const monthly = new Map(monthKeys.map((m) => [m, emptyMonthRow()]));

  for (const s of salesHistory || []) {
    const m = dateKeyFromString(s.date).slice(0, 7);
    if (!monthly.has(m)) continue;
    const row = monthly.get(m);
    row.posRevenue += Number(s.amount) || 0;
    row.posProfit += saleProfit(s);
    row.posCount += 1;
  }

  for (const e of expenses || []) {
    const m = String(e.expenseDate || '').slice(0, 7);
    if (!monthly.has(m)) continue;
    const row = monthly.get(m);
    const amt = Number(e.amount) || 0;
    row.expenses += amt;
    const cat = String(e.category || '');
    if (cat === 'boss') row.expenseBoss += amt;
    else if (cat === 'staff') row.expenseStaff += amt;
    else if (cat === 'office') row.expenseOffice += amt;
  }

  for (const j of printJobs || []) {
    const m = dateKeyFromString(j.createdAt).slice(0, 7);
    if (!monthly.has(m)) continue;
    const row = monthly.get(m);
    row.printBilled += Number(j.totalAmount) || 0;
    row.printCollected += Number(j.paidAmount) || 0;
  }

  for (const o of otherServiceSales || []) {
    const m = String(o.dateKey || '').slice(0, 7);
    if (!monthly.has(m)) continue;
    monthly.get(m).otherServices += Number(o.amountTzs) || 0;
  }

  for (const t of wakalaCashbookRows(transactions || [])) {
    const m = String(t.dateKey || '').slice(0, 7);
    if (!monthly.has(m)) continue;
    const row = monthly.get(m);
    const amt = Number(t.amount) || 0;
    if (t.direction === 'in') row.wakalaIn += amt;
    else row.wakalaOut += amt;
  }

  if (factoryByMonth) {
    for (const [m, report] of Object.entries(factoryByMonth)) {
      if (!monthly.has(m)) continue;
      const totals = report?.totals || {};
      const row = monthly.get(m);
      row.bricksNet = Number(totals.bricksNet) || 0;
      row.deliveryRevenue = Number(totals.deliveryRevenueTzs) || 0;
      row.deliveryPieces = Number(totals.deliveryPieces) || 0;
    }
  }

  const rows = monthKeys.map((month) => {
    const r = monthly.get(month);
    const totalRevenue =
      r.posRevenue + r.printCollected + r.otherServices + r.deliveryRevenue;
    const netEstimate = r.posProfit + r.printCollected + r.otherServices + r.deliveryRevenue - r.expenses;
    return { month, ...r, totalRevenue, netEstimate };
  });

  const totals = rows.reduce(
    (acc, r) => ({
      posRevenue: acc.posRevenue + r.posRevenue,
      posProfit: acc.posProfit + r.posProfit,
      posCount: acc.posCount + r.posCount,
      expenses: acc.expenses + r.expenses,
      expenseBoss: acc.expenseBoss + r.expenseBoss,
      expenseStaff: acc.expenseStaff + r.expenseStaff,
      expenseOffice: acc.expenseOffice + r.expenseOffice,
      printBilled: acc.printBilled + r.printBilled,
      printCollected: acc.printCollected + r.printCollected,
      otherServices: acc.otherServices + r.otherServices,
      wakalaIn: acc.wakalaIn + r.wakalaIn,
      wakalaOut: acc.wakalaOut + r.wakalaOut,
      bricksNet: acc.bricksNet + r.bricksNet,
      deliveryRevenue: acc.deliveryRevenue + r.deliveryRevenue,
      deliveryPieces: acc.deliveryPieces + r.deliveryPieces,
      totalRevenue: acc.totalRevenue + r.totalRevenue,
      netEstimate: acc.netEstimate + r.netEstimate,
    }),
    {
      posRevenue: 0,
      posProfit: 0,
      posCount: 0,
      expenses: 0,
      expenseBoss: 0,
      expenseStaff: 0,
      expenseOffice: 0,
      printBilled: 0,
      printCollected: 0,
      otherServices: 0,
      wakalaIn: 0,
      wakalaOut: 0,
      bricksNet: 0,
      deliveryRevenue: 0,
      deliveryPieces: 0,
      totalRevenue: 0,
      netEstimate: 0,
    }
  );

  const approvedProducts = (products || []).filter((p) => p.approvalStatus !== 'pending');
  const stockValue = approvedProducts.reduce(
    (sum, p) => sum + (Number(p.quantity) || 0) * (Number(p.purchasingPrice) || 0),
    0
  );
  const stockRetailValue = approvedProducts.reduce(
    (sum, p) => sum + (Number(p.quantity) || 0) * (Number(p.sellingPrice) || 0),
    0
  );

  const factory = isBrickFactoryUser(user) && hasTenantModule(user, 'brick');
  const showPrinting = !factory && hasTenantModule(user, 'printing');
  const showWakala = hasTenantModule(user, 'cashbook') && !factory;
  const showDebts = hasTenantModule(user, 'debts');

  return {
    monthKeys,
    periodLabel: periodLabelFromMonths(monthKeys),
    monthCount,
    rows,
    totals,
    stock: {
      productCount: approvedProducts.length,
      stockValue,
      stockRetailValue,
    },
    debts: {
      openCount: debtSummary?.openCount ?? 0,
      totalOwedTzs: debtSummary?.totalOwedTzs ?? 0,
    },
    flags: { factory, showPrinting, showWakala, showDebts },
  };
}
